import * as React from "react";
import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { AppLayout } from "@/components/layout/AppLayout";
import { ModuleLayout, type TabItem } from "@/components/layout/ModuleLayout";
import { ProtectedRoute } from "@/components/layout/ProtectedRoute";
import { RouteFallback } from "@/components/layout/RouteFallback";
import { LoginPage } from "@/pages/LoginPage";
import { DashboardPage } from "@/pages/DashboardPage";
import { useAuth } from "@/contexts/AuthContext";

const NitsModuleLayout = () => {
  const { isAdmin, hasPermission } = useAuth();
  const tabs: TabItem[] = [
    { to: "/nits", label: "NITs", end: true },
    ...(isAdmin || hasPermission("editar_nits")
      ? [
          { to: "/nits/nuevo", label: "Nuevo NIT" },
          { to: "/nits/importar", label: "Importar NITs" },
        ]
      : []),
  ];
  return <ModuleLayout title="NITs" tabs={tabs} />;
};

const SubirFacturasPage = lazy(() =>
  import("@/pages/SubirFacturasPage").then((m) => ({
    default: m.SubirFacturasPage,
  })),
);
const AuditoriaPage = lazy(() =>
  import("@/pages/AuditoriaPage").then((m) => ({ default: m.AuditoriaPage })),
);
const VerFacturaPage = lazy(() =>
  import("@/pages/VerFacturaPage").then((m) => ({ default: m.VerFacturaPage })),
);

const VentasSubirPage = lazy(() =>
  import("@/pages/VentasSubirPage").then((m) => ({
    default: m.VentasSubirPage,
  })),
);
const VentasAuditoriaPage = lazy(() =>
  import("@/pages/VentasAuditoriaPage").then((m) => ({
    default: m.VentasAuditoriaPage,
  })),
);
const VentasVerPage = lazy(() =>
  import("@/pages/VentasVerPage").then((m) => ({ default: m.VentasVerPage })),
);

const ExteriorSubirPage = lazy(() =>
  import("@/pages/ExteriorSubirPage").then((m) => ({
    default: m.ExteriorSubirPage,
  })),
);
const ExteriorAuditoriaPage = lazy(() =>
  import("@/pages/ExteriorAuditoriaPage").then((m) => ({
    default: m.ExteriorAuditoriaPage,
  })),
);
const ExteriorVerPage = lazy(() =>
  import("@/pages/ExteriorVerPage").then((m) => ({ default: m.ExteriorVerPage })),
);

const LegalizacionSubirPage = lazy(() =>
  import("@/pages/LegalizacionSubirPage").then((m) => ({
    default: m.LegalizacionSubirPage,
  })),
);
const LegalizacionAuditoriaPage = lazy(() =>
  import("@/pages/LegalizacionAuditoriaPage").then((m) => ({
    default: m.LegalizacionAuditoriaPage,
  })),
);
const LegalizacionVerPage = lazy(() =>
  import("@/pages/LegalizacionVerPage").then((m) => ({
    default: m.LegalizacionVerPage,
  })),
);

import { UsuariosPage } from "@/pages/UsuariosPage";
import { UsuarioNuevoPage } from "@/pages/UsuarioNuevoPage";
import { UsuarioEditarPage } from "@/pages/UsuarioEditarPage";
import { RolesPage } from "@/pages/RolesPage";
import { RolPermisosPage, UsuarioPermisosPage } from "@/pages/PermisosPages";
import { EmpresasPage } from "@/pages/EmpresasPage";
import { EmpresaFormPage } from "@/pages/EmpresaFormPage";
import { NitsPage } from "@/pages/NitsPage";
import { NitNuevoPage } from "@/pages/NitNuevoPage";
import { NitEditarPage } from "@/pages/NitEditarPage";
import { NitVerPage } from "@/pages/NitVerPage";
import { NitImportarPage } from "@/pages/NitImportarPage";
import { ConfecamarasPage } from "@/pages/ConfecamarasPage";
import { HerramientasPage } from "@/pages/HerramientasPage";

const Protected = ({
  children,
  requireAdmin = false,
  requiredPermission,
}: {
  children: React.ReactNode;
  requireAdmin?: boolean;
  requiredPermission?: string;
}) => (
  <ProtectedRoute
    requireAdmin={requireAdmin}
    requiredPermission={requiredPermission}
  >
    {children}
  </ProtectedRoute>
);

export const App = () => (
  <Routes>
    <Route path="/login" element={<LoginPage />} />

    <Route
      element={
        <ProtectedRoute>
          <AppLayout />
        </ProtectedRoute>
      }
    >
      <Route index element={<DashboardPage />} />

      {/* Facturas de Compra */}
      <Route
        path="facturas"
        element={
          <Protected>
            <Suspense fallback={<RouteFallback />}>
              <ModuleLayout
                title="Facturas de Compra"
                tabs={[
                  { to: "/facturas/subir", label: "Subir" },
                  { to: "/facturas/auditoria", label: "Auditoría" },
                ]}
                wsPath="/ws"
                wsSuccessMessage="Conectado al servidor de procesamiento"
              />
            </Suspense>
          </Protected>
        }
      >
        <Route index element={<Navigate to="/facturas/subir" replace />} />
        <Route
          path="subir"
          element={
            <Suspense fallback={<RouteFallback />}>
              <SubirFacturasPage />
            </Suspense>
          }
        />
        <Route
          path="auditoria"
          element={
            <Suspense fallback={<RouteFallback />}>
              <AuditoriaPage />
            </Suspense>
          }
        />
      </Route>
      <Route
        path="facturas/ver/:id"
        element={
          <Protected>
            <Suspense fallback={<RouteFallback />}>
              <VerFacturaPage />
            </Suspense>
          </Protected>
        }
      />

      {/* Facturas de Ventas */}
      <Route
        path="ventas"
        element={
          <Protected>
            <Suspense fallback={<RouteFallback />}>
              <ModuleLayout
                title="Facturas de Ventas"
                tabs={[
                  { to: "/ventas/subir", label: "Subir" },
                  { to: "/ventas/auditoria", label: "Auditoría" },
                ]}
                wsPath="/ws_ventas"
                wsSuccessMessage="Conectado al servidor de ventas"
              />
            </Suspense>
          </Protected>
        }
      >
        <Route index element={<Navigate to="/ventas/subir" replace />} />
        <Route
          path="subir"
          element={
            <Suspense fallback={<RouteFallback />}>
              <VentasSubirPage />
            </Suspense>
          }
        />
        <Route
          path="auditoria"
          element={
            <Suspense fallback={<RouteFallback />}>
              <VentasAuditoriaPage />
            </Suspense>
          }
        />
      </Route>
      <Route
        path="ventas/ver/:id"
        element={
          <Protected>
            <Suspense fallback={<RouteFallback />}>
              <VentasVerPage />
            </Suspense>
          </Protected>
        }
      />

      {/* Facturas Exterior */}
      <Route
        path="exterior"
        element={
          <Protected>
            <Suspense fallback={<RouteFallback />}>
              <ModuleLayout
                title="Facturas Exterior"
                tabs={[
                  { to: "/exterior/subir", label: "Subir" },
                  { to: "/exterior/auditoria", label: "Auditoría" },
                ]}
                wsPath="/ws_facturas_exterior"
                wsSuccessMessage="Conectado al servidor de exterior"
              />
            </Suspense>
          </Protected>
        }
      >
        <Route index element={<Navigate to="/exterior/subir" replace />} />
        <Route
          path="subir"
          element={
            <Suspense fallback={<RouteFallback />}>
              <ExteriorSubirPage />
            </Suspense>
          }
        />
        <Route
          path="auditoria"
          element={
            <Suspense fallback={<RouteFallback />}>
              <ExteriorAuditoriaPage />
            </Suspense>
          }
        />
      </Route>
      <Route
        path="exterior/ver/:id"
        element={
          <Protected>
            <Suspense fallback={<RouteFallback />}>
              <ExteriorVerPage />
            </Suspense>
          </Protected>
        }
      />

      {/* Legalización de Gastos */}
      <Route
        path="legalizacion"
        element={
          <Protected>
            <Suspense fallback={<RouteFallback />}>
              <ModuleLayout
                title="Legalización de Gastos"
                tabs={[
                  { to: "/legalizacion/subir", label: "Subir" },
                  { to: "/legalizacion/auditoria", label: "Auditoría" },
                ]}
                wsPath="/api/v1/legalizacion/ws"
                wsSuccessMessage="Conectado al servidor de legalización"
              />
            </Suspense>
          </Protected>
        }
      >
        <Route index element={<Navigate to="/legalizacion/subir" replace />} />
        <Route
          path="subir"
          element={
            <Suspense fallback={<RouteFallback />}>
              <LegalizacionSubirPage />
            </Suspense>
          }
        />
        <Route
          path="auditoria"
          element={
            <Suspense fallback={<RouteFallback />}>
              <LegalizacionAuditoriaPage />
            </Suspense>
          }
        />
      </Route>
      <Route
        path="legalizacion/ver/:id"
        element={
          <Protected>
            <Suspense fallback={<RouteFallback />}>
              <LegalizacionVerPage />
            </Suspense>
          </Protected>
        }
      />

      {/* Gestión de Usuarios */}
      <Route
        path="usuarios"
        element={
          <Protected requiredPermission="ver_usuarios">
            <ModuleLayout
              title="Gestión de Usuarios"
              tabs={[
                { to: "/usuarios", label: "Usuarios", end: true },
                { to: "/usuarios/roles", label: "Roles" },
              ]}
            />
          </Protected>
        }
      >
        <Route path="" element={<UsuariosPage />} />
        <Route path="roles" element={<RolesPage />} />
      </Route>
      <Route
        path="usuarios/nuevo"
        element={
          <Protected requiredPermission="editar_usuarios">
            <UsuarioNuevoPage />
          </Protected>
        }
      />
      <Route
        path="usuarios/:id/editar"
        element={
          <Protected requiredPermission="editar_usuarios">
            <UsuarioEditarPage />
          </Protected>
        }
      />
      <Route
        path="usuarios/:id/permisos"
        element={
          <Protected requiredPermission="editar_usuarios">
            <UsuarioPermisosPage />
          </Protected>
        }
      />
      <Route
        path="usuarios/roles/:id/permisos"
        element={
          <Protected requiredPermission="editar_usuarios">
            <RolPermisosPage />
          </Protected>
        }
      />

      {/* Gestión de Empresas */}
      <Route
        path="empresas"
        element={
          <Protected requiredPermission="ver_empresas">
            <ModuleLayout
              title="Gestión de Empresas"
              tabs={[
                { to: "/empresas", label: "Empresas", end: true },
                { to: "/empresas/nueva", label: "Nueva Empresa" },
              ]}
            />
          </Protected>
        }
      >
        <Route path="" element={<EmpresasPage />} />
        <Route path="nueva" element={<EmpresaFormPage />} />
      </Route>
      <Route
        path="empresas/:id/editar"
        element={
          <Protected requiredPermission="editar_empresas">
            <EmpresaFormPage />
          </Protected>
        }
      />

      {/* NITs */}
      <Route
        path="nits"
        element={
          <Protected>
            <NitsModuleLayout />
          </Protected>
        }
      >
        <Route path="" element={<NitsPage />} />
        <Route
          path="nuevo"
          element={
            <Protected requiredPermission="editar_nits">
              <NitNuevoPage />
            </Protected>
          }
        />
        <Route
          path="importar"
          element={
            <Protected requiredPermission="editar_nits">
              <NitImportarPage />
            </Protected>
          }
        />
      </Route>
      <Route
        path="nits/:nit/ver"
        element={
          <Protected>
            <NitVerPage />
          </Protected>
        }
      />
      <Route
        path="nits/:nit/editar"
        element={
          <Protected requiredPermission="editar_nits">
            <NitEditarPage />
          </Protected>
        }
      />

      {/* Confecámaras */}
      <Route
        path="confecamaras"
        element={
          <Protected>
            <ConfecamarasPage />
          </Protected>
        }
      />

      {/* Herramientas */}
      <Route
        path="herramientas"
        element={
          <Protected>
            <HerramientasPage />
          </Protected>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Route>
  </Routes>
);